/**
 * ImportFocusedSubgraph — focused import-neighbourhood view.
 * Issue #565 Phase 3. Port of FocusedSubgraph.tsx for import graph edges.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { importGraphNodeTypes } from './ImportGraphNodes';
import type { ImportGraphData, ImportGraphEdge } from './importGraphFlowConverter';
import { ROLE_COLORS } from './importGraphFlowConverter';
import type { DiagramMetadata } from '@veodiagram/shared';
import { applyLayout, type LayoutDirection, type LayoutAlgorithm } from '../shared/layoutEngine';
import { SubgraphLayoutControls } from '../shared/SubgraphLayoutControls';
import { FloatingEdge } from '../shared/FloatingEdge';

const importSubgraphEdgeTypes = { floating: FloatingEdge };

interface ImportFocusedSubgraphProps {
  data: ImportGraphData;
  rootFileId: string;
  direction?: 'importers' | 'imports' | 'both';
  maxDepth?: number;
  onSelectFile?: (fileId: string) => void;
  onBack?: () => void;
  onClose?: () => void;
  // Issue #570 P0: breadcrumb history from parent
  historyFileIds?: string[];
  onNavigateToHistoryIndex?: (i: number) => void;
}

export function ImportFocusedSubgraph({
  data,
  rootFileId,
  direction = 'both',
  maxDepth = 2,
  onSelectFile,
  onBack,
  onClose,
  historyFileIds,
  onNavigateToHistoryIndex,
}: ImportFocusedSubgraphProps) {
  const [depth, setDepth] = useState(maxDepth);
  const [selectedDirection, setSelectedDirection] = useState(direction);
  const [searchQuery, setSearchQuery] = useState('');
  const [layoutDirection, setLayoutDirection] = useState<LayoutDirection>('LR');
  const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('dagre');
  const [hideBarrels, setHideBarrels] = useState(false);
  const [showMiniMap, setShowMiniMap] = useState(true);
  const [layoutPositions, setLayoutPositions] = useState<Map<string, { x: number; y: number }> | null>(null);
  const [isLayouting, setIsLayouting] = useState(false);

  // Build edge lookup maps
  const edgesBySource = useMemo(() => {
    const map = new Map<string, ImportGraphEdge[]>();
    for (const e of data.graph.edges) {
      const arr = map.get(e.source) || [];
      arr.push(e);
      map.set(e.source, arr);
    }
    return map;
  }, [data.graph.edges]);

  const edgesByTarget = useMemo(() => {
    const map = new Map<string, ImportGraphEdge[]>();
    for (const e of data.graph.edges) {
      const arr = map.get(e.target) || [];
      arr.push(e);
      map.set(e.target, arr);
    }
    return map;
  }, [data.graph.edges]);

  const nodeMap = useMemo(
    () => new Map(data.graph.nodes.map(n => [n.id, n])),
    [data.graph.nodes]
  );

  // BFS subgraph extraction
  const { subgraphNodes, subgraphEdges, nodeDirection } = useMemo(() => {
    const included = new Set<string>([rootFileId]);
    // Track whether each node was reached as an importer or import of the root
    const nodeDirection = new Map<string, 'importer' | 'import' | 'root'>();
    nodeDirection.set(rootFileId, 'root');

    const queue: Array<{ id: string; d: number; dir: 'importer' | 'import' }> = [];

    if (selectedDirection === 'importers' || selectedDirection === 'both') {
      for (const e of edgesByTarget.get(rootFileId) || [])
        queue.push({ id: e.source, d: 1, dir: 'importer' });
    }
    if (selectedDirection === 'imports' || selectedDirection === 'both') {
      for (const e of edgesBySource.get(rootFileId) || [])
        queue.push({ id: e.target, d: 1, dir: 'import' });
    }

    while (queue.length > 0) {
      const { id, d, dir } = queue.shift()!;
      if (included.has(id) || d > depth) continue;
      const node = nodeMap.get(id);
      if (hideBarrels && node?.isBarrel) continue;
      included.add(id);
      nodeDirection.set(id, dir);
      if (d < depth) {
        if (dir === 'importer') {
          for (const e of edgesByTarget.get(id) || [])
            if (!included.has(e.source)) queue.push({ id: e.source, d: d + 1, dir });
        } else {
          for (const e of edgesBySource.get(id) || [])
            if (!included.has(e.target)) queue.push({ id: e.target, d: d + 1, dir });
        }
      }
    }

    const nodes = data.graph.nodes.filter(n => included.has(n.id));
    const edges = data.graph.edges.filter(e => included.has(e.source) && included.has(e.target));
    return { subgraphNodes: nodes, subgraphEdges: edges, nodeDirection };
  }, [data, rootFileId, depth, selectedDirection, edgesBySource, edgesByTarget, hideBarrels, nodeMap]);

  // Async layout: run applyLayout whenever subgraph or layout settings change
  useEffect(() => {
    if (subgraphNodes.length === 0) {
      setLayoutPositions(new Map());
      return;
    }
    let cancelled = false;
    setIsLayouting(true);

    const layoutNodes = subgraphNodes.map(n => ({
      id: n.id,
      type: 'service' as const,
      label: n.name,
      position: { x: 0, y: 0 },
      // Use actual rendered sizes so dagre spaces nodes correctly (issue #611)
      width: 220,
      height: 90,
    }));
    const layoutEdges = subgraphEdges.map(e => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'default' as const,
      animated: false,
    }));

    applyLayout(
      layoutNodes as Parameters<typeof applyLayout>[0],
      layoutEdges as Parameters<typeof applyLayout>[1],
      { title: 'subgraph', layoutDirection, layoutAlgorithm } as DiagramMetadata,
    ).then(result => {
      if (cancelled) return;
      setLayoutPositions(new Map(result.map(n => [n.id, n.position])));
      setIsLayouting(false);
    }).catch(() => {
      if (!cancelled) setIsLayouting(false);
    });

    return () => { cancelled = true; };
  }, [subgraphNodes, subgraphEdges, layoutDirection, layoutAlgorithm]);

  // Build React Flow nodes from layout positions
  const flowNodes: Node[] = useMemo(() => {
    if (!layoutPositions) return [];
    const query = searchQuery.trim().toLowerCase();
    const hasSearch = query.length > 0;

    return subgraphNodes.map(n => {
      const pos = layoutPositions.get(n.id) ?? { x: 0, y: 0 };
      const matches = hasSearch && (
        n.name.toLowerCase().includes(query) ||
        n.filePath.toLowerCase().includes(query)
      );
      return {
        id: n.id,
        type: 'importGraphNode',
        position: pos,
        data: {
          name: n.name,
          filePath: n.filePath,
          role: n.role,
          layer: n.layer,
          exportCount: n.exportCount,
          importCount: n.importCount,
          importedBy: n.importedBy,
          imports: n.imports,
          isBarrel: n.isBarrel,
          hasCircularDep: n.hasCircularDep,
          isEntryPoint: n.isEntryPoint,
          isRoot: n.id === rootFileId,
          dimmed: hasSearch && !matches,
          highlighted: hasSearch && matches,
        },
        style: hasSearch && !matches ? { opacity: 0.3 } : undefined,
      };
    });
  }, [layoutPositions, subgraphNodes, rootFileId, searchQuery]);

  // Build React Flow edges with direction-based colour coding
  const flowEdges: Edge[] = useMemo(() => subgraphEdges.map(e => {
    // Circular and reexport override direction colours
    const sourceDir = nodeDirection.get(e.source);
    const directionColour = sourceDir === 'importer'
      ? '#3b82f6'   // blue — importer → root
      : '#22c55e';  // green — root → import

    const colour = e.isCircular
      ? '#ef4444'             // red — circular dep (overrides direction)
      : e.type === 'reexport'
        ? '#06b6d4'           // cyan — re-export (overrides direction)
        : directionColour;    // blue or green based on BFS direction

    return {
      id: e.id,
      source: e.source,
      target: e.target,
      type: 'floating',
      animated: e.type === 'reexport',
      style: {
        stroke: colour,
        strokeWidth: e.isCircular ? 2 : 1,
        strokeDasharray: e.type === 'typeOnly' ? '5 5' : undefined,
      },
      markerEnd: { type: 'arrowclosed', color: colour, width: 14, height: 14 },
      label: e.imports.length > 0 ? (e.imports.length > 3 ? `${e.imports.length} items` : e.imports.join(', ')) : undefined,
      labelStyle: { fontSize: 10 },
    };
  }), [subgraphEdges, nodeDirection]);

  const [nodes, setNodes, onNodesChange] = useNodesState(flowNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(flowEdges);

  useEffect(() => {
    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [flowNodes, flowEdges, setNodes, setEdges]);

  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    onSelectFile?.(node.id);
  }, [onSelectFile]);

  const rootNode = nodeMap.get(rootFileId);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex items-center gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            Subgraph: {rootNode?.name || rootFileId}
          </h3>
          {/* Issue #570 P0: Breadcrumb showing subgraph navigation history */}
          {historyFileIds && historyFileIds.length > 0 && (
            <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5 overflow-x-auto">
              {historyFileIds.map((id, i) => (
                <React.Fragment key={`${id}-${i}`}>
                  <button onClick={() => onNavigateToHistoryIndex?.(i)}
                    className="text-blue-600 hover:underline truncate max-w-[100px]"
                    title={nodeMap.get(id)?.filePath}>
                    {nodeMap.get(id)?.name ?? id}
                  </button>
                  <span className="text-gray-400">›</span>
                </React.Fragment>
              ))}
              <span className="font-medium text-gray-700 truncate">{rootNode?.name}</span>
            </div>
          )}
          <p className="text-sm text-gray-500">
            {subgraphNodes.length} files · {subgraphEdges.length} imports
          </p>
        </div>

        <select
          value={selectedDirection}
          onChange={e => setSelectedDirection(e.target.value as typeof selectedDirection)}
          className="px-3 py-1 border border-gray-300 rounded text-sm"
        >
          <option value="both">Both directions</option>
          <option value="importers">Importers only</option>
          <option value="imports">Imports only</option>
        </select>

        <input
          type="text"
          placeholder="Search files..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded text-sm w-36"
        />

        <SubgraphLayoutControls
          layoutDirection={layoutDirection}
          layoutAlgorithm={layoutAlgorithm}
          onDirectionChange={setLayoutDirection}
          onAlgorithmChange={setLayoutAlgorithm}
          isLayouting={isLayouting}
        />

        <label className="flex items-center gap-1 text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={hideBarrels} onChange={e => setHideBarrels(e.target.checked)} className="rounded" />
          Hide barrels
        </label>

        <label className="flex items-center gap-1 text-sm text-gray-600 cursor-pointer select-none">
          <input type="checkbox" checked={showMiniMap} onChange={e => setShowMiniMap(e.target.checked)} className="rounded" />
          Minimap
        </label>

        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Depth:</label>
          <input
            type="range" min="1" max="5" value={depth}
            onChange={e => setDepth(parseInt(e.target.value))}
            className="w-20"
          />
          <span className="text-sm text-gray-600 w-4">{depth}</span>
        </div>

        {onBack && (
          <button onClick={onBack} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm">
            Back
          </button>
        )}
        {onClose && (
          <button onClick={onClose} className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300 text-sm">
            Close
          </button>
        )}
      </div>

      {subgraphNodes.length > 100 && (
        <div className="px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-sm">
          Large subgraph ({subgraphNodes.length} nodes) — consider reducing depth.
        </div>
      )}

      <div className="flex-1" style={{ minHeight: 400 }}>
        <ReactFlow
          nodes={nodes} edges={edges}
          onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={importGraphNodeTypes}
          edgeTypes={importSubgraphEdgeTypes}
          fitView minZoom={0.1} maxZoom={2}
        >
          <Background />
          <Controls />
          {showMiniMap && (
            <MiniMap
              nodeColor={node => node.data?.isRoot ? '#3b82f6' : (ROLE_COLORS[node.data?.role as string] || '#94a3b8')}
              maskColor="rgba(0,0,0,0.1)"
              style={{ width: 160, height: 100 }}
              {...{ pannable: true, zoomable: true }}
            />
          )}
        </ReactFlow>
      </div>

      {/* Legend */}
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 flex items-center gap-4 text-xs flex-wrap">
        <span className="text-gray-500">Nodes:</span>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded bg-blue-500" />
          <span className="text-gray-600">Root</span>
        </div>
        {Object.entries(ROLE_COLORS).slice(0, 5).map(([role, color]) => (
          <div key={role} className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
            <span className="text-gray-500 capitalize">{role}</span>
          </div>
        ))}
        <span className="text-gray-400">|</span>
        <span className="text-gray-500">Edges:</span>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-blue-500" />
          <span className="text-gray-600">Importer → Root</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-green-500" />
          <span className="text-gray-600">Root → Import</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-red-500" />
          <span className="text-gray-600">Circular</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-cyan-500" />
          <span className="text-gray-600">Re-export</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-8 h-0.5 bg-gray-400" style={{ borderTop: '2px dashed #9ca3af', height: 0 }} />
          <span className="text-gray-600">Type-only</span>
        </div>
      </div>
    </div>
  );
}
